import { ChangeEventHandler, useMemo } from 'react';
import { Box, Button, Dropdown, Group, IconThreeDotsVer, IconTrash, Select, Typography } from '@screentone/core';

import {
  getQueryRuleByArrayName,
  QueryRule,
  QueryRuleArrayNameType,
  QueryRuleOperatorType
} from 'components/query-rules-form/queryRulesFormUtils';
import { useAllessehContentUpdate } from 'contexts/allesseh-content-update/useAllessehContentUpdate';
import { useSummarianContext } from 'contexts/summarian/useSummarianContext';
import { useConvertedProperty } from 'hooks';
import { AllessehContent, AllessehContentQueryBody } from 'hooks/useAllessehContentQuery';
import { getSentence } from 'utils/text';
import { QueryRuleFormKeyConfig } from './query-rule-configs/types';
import styles from './QueryRuleForm.module.scss';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import {
  getFormComponentByQueryRuleFormType,
  getKeysByQueryRuleArrayNameType,
  getOperatorsByType
} from './queryRuleFormUtils';

import { useAltSummFields } from 'contexts/summarian/summarian.utils';

const CONTENT_TYPE = 'ContentType';

const ExcludedItemHeadline = ({ content }: { content: AllessehContent }) => {
  const { selectedAltSummVariant: altSummVariant } = useSummarianContext();
  const { headline } = useAltSummFields(content, altSummVariant);
  return (
    <Typography data-testid="query-rule-form-type-title" margin={{ vertical: 'sm' }} variant="note">
      {headline}
    </Typography>
  );
};

interface QueryRuleProps {
  queryArrayType: QueryRuleArrayNameType;
  rule: QueryRule;
  excludedData?: AllessehContent[] | undefined;
  onChange: ((rule: QueryRule) => void) | null;
  onRemove: (() => void) | null;
  fullAllessehQuery: AllessehContentQueryBody;
  queryRuleFilter?: string[];
  disabled?: boolean;
  // Fetched from publication-setting by the parent (QueryRulesForm). Passed
  // down so we don't observe the same react-query subscription per rendered
  // rule. Falls back to the static per-pub config when undefined/empty.
  fetchedQueryRuleSchema?: Record<string, unknown> | null;
}

export const QueryRuleForm = ({
  rule,
  onChange,
  onRemove,
  queryArrayType,
  fullAllessehQuery,
  excludedData,
  queryRuleFilter,
  disabled,
  fetchedQueryRuleSchema
}: QueryRuleProps) => {
  const currentProperty = useConvertedProperty();

  const id = rule.value;
  const article = excludedData?.find((item: AllessehContent) => item.data.id === id);
  const updatedContent = useAllessehContentUpdate(article?.data.id ?? '', {
    enabled: !!article?.data.id,
    initialContent: article
  });
  const allessehContent = useMemo(() => updatedContent ?? article ?? null, [article, updatedContent]);

  // do not display contentType in queryRules
  if (rule.key === CONTENT_TYPE) return null;

  const handleRuleKeyChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const newKey = e.target.value;
    onChange?.({ key: newKey, value: '', operator: QueryRuleOperatorType.EQUAL });
  };

  const handleChangeOperator = (newOperator: QueryRuleOperatorType) => {
    onChange?.({ ...rule, operator: newOperator });
  };

  const handleChangeValue = (newValue: string) => {
    onChange?.({ ...rule, value: newValue });
  };

  const handleRemoveRule = () => {
    onRemove?.();
  };

  const queryRuleConfig = getQueryRuleByArrayName(currentProperty, queryRuleFilter, fetchedQueryRuleSchema);
  const ruleMapping = queryRuleConfig[rule.key] as QueryRuleFormKeyConfig | undefined;
  const InputComponent = getFormComponentByQueryRuleFormType(ruleMapping?.queryRuleFormType);

  // If rule mapping is commented out, return null
  // So we don't show the rule form
  if (!InputComponent) {
    return null;
  }
  const showHeadline =
    queryArrayType === QueryRuleArrayNameType.NOT &&
    rule.key === 'UpstreamOriginId' &&
    rule.operator === QueryRuleOperatorType.NOT_EQUAL;

  return (
    <div className={styles.wrapper} data-testid="query-rule-form-container">
      <Select
        data-testid="query-rule-form-type"
        value={rule.key}
        onChange={handleRuleKeyChange}
        className={styles.select}
        disabled={disabled}
      >
        {getKeysByQueryRuleArrayNameType(queryArrayType, queryRuleConfig).map((key) => (
          <option data-testid="query-rule-form-type-option" key={key} value={key} disabled={key === CONTENT_TYPE}>
            {queryRuleConfig[key].label ?? getSentence(key)}
          </option>
        ))}
      </Select>
      <Box data-testid="query-rule-form-add-rule" padding={{ all: 'sm' }} className={styles.buttonGroup}>
        {getOperatorsByType(queryArrayType, ruleMapping?.queryRuleFormType).map(
          (operator: QueryRuleOperatorType, index) => (
            <Button
              data-testid="query-rule-form-add-rule-button"
              active={operator === rule.operator}
              key={index}
              justified="center"
              fullWidth
              tertiary
              onClick={() => handleChangeOperator(operator)}
              disabled={disabled}
            >
              {operator}
            </Button>
          )
        )}
      </Box>
      <div data-testid="query-rule-form-type-value" className={styles.inputComponent}>
        <InputComponent
          data-testid="query-rule-form-type-value-input"
          value={rule.value}
          onChange={handleChangeValue}
          ruleConfig={queryRuleConfig[rule.key]}
          allessehKey={rule.key}
          fullAllessehQuery={fullAllessehQuery}
          disabled={!!disabled}
        />
        {showHeadline && allessehContent && <ExcludedItemHeadline content={allessehContent} />}
      </div>
      {onRemove && (
        <Dropdown
          data-testid="query-rule-form-toggle-menu"
          padding={{ all: 'none' }}
          position="none"
          trigger={<IconThreeDotsVer color="asphalt" />}
        >
          <Group data-testid="query-rule-form-toggle-remove" margin={{ all: 'md' }} gap="xs">
            <Button
              data-testid="query-rule-form-toggle-remove-button"
              tertiary
              icon={IconTrash}
              color="lava"
              onClick={handleRemoveRule}
            >
              Remove
            </Button>
          </Group>
        </Dropdown>
      )}
    </div>
  );
};
